import {
  applyPolyfills,
  isTMA,
  retrieveLaunchParams,
  retrieveRawInitData,
} from "@telegram-apps/sdk";
import { isColorDark, type ColorScheme, type ThemeParams } from "./theme";

export interface LaunchContext {
  /** Raw initData for the `Authorization: tma <…>` header, or null outside Telegram. */
  initDataRaw: string | null;
  themeParams: ThemeParams;
  colorScheme: ColorScheme;
  inTelegram: boolean;
  /** The `startapp` deep-link payload (e.g. a `t_<game>_<roomId>` private-table code). */
  startParam: string | null;
  /** Telegram WebApp platform (`ios` / `android` / `tdesktop` / `web` …), or null outside Telegram. */
  platform: string | null;
  /** Telegram WebApp / Bot-API version (e.g. `7.10`), or null outside Telegram. */
  version: string | null;
  /**
   * TELEGRAM LAUNCHED US AND WE COULD NOT READ IT — the one state the app used to have no name
   * for, and the reason a player inside Telegram was told to open the app from Telegram.
   *
   * `isTMA()` is false for a plain browser AND for an engine that cannot parse the launch params
   * (see {@link ensureSdkPolyfills}), so the SDK's own answer cannot tell the two apart — its
   * error text even guesses the wrong one («Perhaps, you have opened your app outside Telegram?»).
   * What separates them is whether a `tgWebAppData` is VISIBLY there in the sources the SDK itself
   * reads: this is true only when one is, and we still ended up with no credential.
   *
   * Read by `bootErrorMessage` to replace the «open it from Telegram» line, which is actively
   * misleading for someone who already did.
   */
  launchParamsUnusable: boolean;
}

/** Minimal view of the launch params we consume (the SDK's type is broader). */
interface RawLaunchParams {
  tgWebAppThemeParams?: ThemeParams;
  tgWebAppStartParam?: string;
  tgWebAppPlatform?: string;
  tgWebAppVersion?: string;
}

/**
 * Read the `startapp` (or `tgWebAppStartParam`) payload off the app's own URL.
 *
 * THIS IS NOT JUST A DEV FALLBACK — it is the ONLY source for a Mini App opened
 * from a `web_app` BUTTON, which is how every table invite arrives (the bot's
 * «🎮 ورود به میز» glass button and the invite PV's open-app button both point at
 * `<webAppUrl>?startapp=t_<game>_<roomId>`). Telegram fills `tgWebAppStartParam`
 * in the launch params ONLY for `t.me` deep links (`t.me/<bot>?startapp=…`); for a
 * button it just opens the given URL verbatim and appends its own
 * `#tgWebAppData=…` hash. So the launch params carry NO start param, and reading
 * only them silently dropped the invite: the friend tapped «ورود به میز» and
 * landed in the LOBBY.
 */
function startParamFromUrl(): string | null {
  if (typeof location === "undefined") return null;
  const q = new URLSearchParams(location.search);
  return q.get("tgWebAppStartParam") ?? q.get("startapp") ?? null;
}

function prefersDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

function schemeFromTheme(theme: ThemeParams): ColorScheme {
  const bg = theme["bg_color"] ?? theme["bgColor"];
  if (bg) return isColorDark(bg) ? "dark" : "light";
  return prefersDark() ? "dark" : "light";
}

/**
 * Install the SDK's own polyfills before ANY of its entry points run.
 *
 * ⚠️ THIS IS LOAD-BEARING, AND IT IS ONE LINE OF SOMEBODY ELSE'S CODE. `@telegram-apps/transformers`
 * parses the launch params with a valibot key check that calls a bare `Object.hasOwn` — a **Chrome
 * 93** API — and `@telegram-apps/bridge` ships the polyfill for exactly that, as an EXPORTED
 * FUNCTION nobody was calling. Without it, on any Android System WebView below Chrome 93 (Huawei
 * without Play Services, Redmi 7A, …) the parse throws a TypeError, `isTMA()` swallows it
 * (`try { retrieveLaunchParams(); return true } catch { return false }`) and the app concludes it
 * is not in Telegram at all: no `Authorization` header, a 401 «Missing Telegram initData», and the
 * player is told to open from Telegram — which is where they already are.
 *
 * MEASURED on the deployed bundle 2026-09-06 (`apps/web/tools/boot-error-probes/`): with every
 * post-Chrome-88 API removed the credential still reaches the api **as long as this runs** — it is
 * the only thing in the way. On prod that day, Chrome 87/88 had 0 successful `/api/me` from 3
 * clients while Chrome >= 93 had 2,285 and not one failure.
 *
 * ⚠️ CALL IT FROM EVERY SDK ENTRY POINT, NOT ONCE AT MODULE SCOPE. Module scope makes the fix
 * depend on IMPORT ORDER — invisible, and undone by any reshuffle of `main.tsx`'s imports. It is
 * idempotent (`Object.hasOwn ||= …`) and costs a property read, so the safe thing is to call it
 * wherever the SDK is about to be used: here, and in `viewport.ts` before `init()`.
 */
export function ensureSdkPolyfills(): void {
  applyPolyfills();
}

/**
 * Did Telegram put a launch payload where the SDK could have found it?
 *
 * The same two sources `retrieveLaunchParams` reads, in the same order: the current URL, and the
 * PERFORMANCE NAVIGATION ENTRY — which still carries the original URL after Telegram (or our own
 * router) has cleaned the hash away, and is why a reload does not lose the answer.
 */
function telegramLaunchPayloadPresent(): boolean {
  const sources: string[] = [];
  try {
    if (typeof location !== "undefined") sources.push(location.href ?? "");
  } catch {
    /* a location-less environment cannot have been launched by Telegram */
  }
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav) sources.push(nav.name ?? "");
  } catch {
    /* no Performance Timeline (older engines) — the URL above is the answer then */
  }
  return sources.some((s) => s.includes("tgWebAppData="));
}

/**
 * Resolve everything we need to boot from the Telegram launch params. Uses the
 * bundled `@telegram-apps/sdk` (never a runtime fetch from telegram.org). Outside
 * Telegram — local dev, tests, a plain browser — it degrades gracefully, taking
 * initData from `VITE_DEV_INIT_DATA` if provided.
 */
export function resolveLaunchContext(): LaunchContext {
  ensureSdkPolyfills();
  if (isTMA()) {
    try {
      const params = retrieveLaunchParams() as unknown as RawLaunchParams;
      const themeParams = params.tgWebAppThemeParams ?? {};
      const initDataRaw = retrieveRawInitData() ?? null;
      return {
        initDataRaw,
        themeParams,
        colorScheme: schemeFromTheme(themeParams),
        inTelegram: true,
        // In Telegram and STILL with no credential: rare, but it is the same dead end for the
        // player as the parse failure below, so it gets the same honest sentence.
        launchParamsUnusable: initDataRaw === null && telegramLaunchPayloadPresent(),
        // Launch params first (a real `t.me/<bot>?startapp=…` deep link), then the
        // app's own URL — see `startParamFromUrl`: a `web_app` BUTTON carries the
        // payload only there, and every table invite is a web_app button.
        startParam: params.tgWebAppStartParam ?? startParamFromUrl(),
        platform: params.tgWebAppPlatform ?? null,
        version: params.tgWebAppVersion ?? null,
      };
    } catch {
      // fall through to the dev fallback
    }
  }
  const initDataRaw = import.meta.env.VITE_DEV_INIT_DATA ?? null;
  return {
    initDataRaw,
    themeParams: {},
    colorScheme: prefersDark() ? "dark" : "light",
    inTelegram: false,
    startParam: startParamFromUrl(),
    platform: null,
    version: null,
    // A plain browser reaches here too, and it is NOT broken — nobody launched it. The payload
    // check is the whole difference, and it is why this cannot be `!inTelegram`.
    launchParamsUnusable: initDataRaw === null && telegramLaunchPayloadPresent(),
  };
}
